import {type BerryNamespace, berryNamespacesSet, type ValidSubdomain} from './BerryConstants'
import {toTitleCase} from './stringUtils'

/**
 * Which deployment a browser page is running in, read off its hostname. Anything not recognised
 * as a non-production host — `notability.com`, `www.`, vanity hosts, unknown domains — is
 * `production`, so a miss can only ever leave a page undecorated, never mislabel production.
 */
export type WebEnvironment =
  | {readonly kind: 'production'}
  | {readonly kind: Extract<ValidSubdomain, 'staging'>}
  | {readonly kind: 'berry'; readonly namespace: BerryNamespace}
  /** A Cloud Run web preview: `c<sha>-<branch>.staging.notability.com` (app) or `m…` (marketing). */
  | {readonly kind: 'preview'}
  /** A dev server: `localhost`, a `*.localhost` alias, or a raw IP such as Vite's `--host` LAN address. */
  | {readonly kind: 'local'}

const PRODUCTION: WebEnvironment = {kind: 'production'}
const STAGING: WebEnvironment = {kind: 'staging'}
const PREVIEW: WebEnvironment = {kind: 'preview'}
const LOCAL: WebEnvironment = {kind: 'local'}

const NOTABILITY_SUFFIX = '.notability.com'
const PREVIEW_HOSTNAME = /^[cm][0-9a-f]+-[a-z0-9][a-z0-9-]*\.staging\.notability\.com$/
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/

export function webEnvironmentFromHostname(rawHostname: string): WebEnvironment {
  const hostname = rawHostname.trim().toLowerCase().replace(/\.$/, '')
  if (hostname === 'localhost' || hostname.endsWith('.localhost') || hostname === '::1' || hostname === '[::1]') {
    return LOCAL
  }
  if (IPV4.test(hostname)) {
    return LOCAL
  }
  if (PREVIEW_HOSTNAME.test(hostname)) {
    return PREVIEW
  }
  if (!hostname.endsWith(NOTABILITY_SUFFIX)) {
    return PRODUCTION
  }
  const subdomain = hostname.slice(0, -NOTABILITY_SUFFIX.length)
  if (subdomain === 'staging') {
    return STAGING
  }
  if (isBerryNamespace(subdomain)) {
    return {kind: 'berry', namespace: subdomain}
  }
  return PRODUCTION
}

function isBerryNamespace(value: string): value is BerryNamespace {
  return berryNamespacesSet.has(value as BerryNamespace)
}

export interface WebEnvironmentLabelOptions {
  /** The backend the page talks to; a dev server pointed at a deployment reads `Local → Boysenberry`. */
  apiServiceUrl?: string
  /** Baked into preview builds at image build time; a preview without one is just `Preview`. */
  previewPrNumber?: string
}

/**
 * What to show for the environment in browser chrome — `Staging`, `Boysenberry`, `Local`,
 * `Preview #62590` — or `undefined` for production, which is never decorated.
 */
export function webEnvironmentLabel(
  environment: WebEnvironment,
  {apiServiceUrl, previewPrNumber}: WebEnvironmentLabelOptions = {}
): string | undefined {
  switch (environment.kind) {
    case 'production':
      return undefined
    case 'staging':
      return 'Staging'
    case 'berry':
      return toTitleCase(environment.namespace)
    case 'preview': {
      const prNumber = previewPrNumber?.trim()
      return prNumber ? `Preview #${prNumber}` : 'Preview'
    }
    case 'local': {
      const backend = backendLabel(apiServiceUrl)
      return backend ? `Local → ${backend}` : 'Local'
    }
  }
}

function backendLabel(apiServiceUrl: string | undefined): string | undefined {
  if (!apiServiceUrl) return undefined
  let hostname: string
  try {
    hostname = new URL(apiServiceUrl).hostname
  } catch {
    return undefined
  }
  const backend = webEnvironmentFromHostname(hostname)
  if (backend.kind === 'local') return undefined
  // A local page talking to notability.com is the one case worth naming production.
  return backend.kind === 'production' ? 'Production' : webEnvironmentLabel(backend)
}

/**
 * The directory under `favicons/` holding an environment's icon art, or `undefined` for
 * production. Local and preview builds share one icon: both are "the bundle you are editing".
 */
export function webEnvironmentFaviconSlug(environment: WebEnvironment): string | undefined {
  switch (environment.kind) {
    case 'production':
      return undefined
    case 'staging':
      return 'staging'
    case 'berry':
      return environment.namespace
    case 'preview':
    case 'local':
      return 'local'
  }
}

const TITLE_SEPARATORS = [' | ', ' - ', ' – ', ' — ']
const BRAND = 'Notability'

/**
 * Slots the environment label in ahead of the brand: `Home | Notability` becomes
 * `Home | Staging | Notability`, `Templates - Notability Gallery` becomes
 * `Templates - Staging - Notability Gallery`. A title that doesn't end on the brand
 * (`Notability for Students — Study better.`, a bare collection name) gets the label in front,
 * so it never lands mid-phrase.
 */
export function environmentTitle(title: string, label: string | undefined): string {
  if (!label) return title
  let split = -1
  let separator = ''
  for (const candidate of TITLE_SEPARATORS) {
    const index = title.lastIndexOf(`${candidate}${BRAND}`)
    if (index > split) {
      split = index
      separator = candidate
    }
  }
  if (split < 0) {
    return `${label} | ${title}`
  }
  return `${title.slice(0, split)}${separator}${label}${title.slice(split)}`
}
